import { useEffect, useState, useCallback } from "react";
import api, { formatApiError, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { IDS } from "@/lib/testIds";
import { DownloadSimple, Printer, MagnifyingGlass } from "@phosphor-icons/react";

export default function QRPrint() {
  const [kind, setKind] = useState("variant");
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [q, setQ] = useState("");
  const [cols, setCols] = useState(3);
  const [rows, setRows] = useState(6);
  const [w, setW] = useState(2.5);
  const [h, setH] = useState(1.75);
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    if (kind === "variant") {
      const { data } = await api.get(`/items?limit=500${q ? `&q=${encodeURIComponent(q)}` : ""}`);
      setItems(data.map((v) => ({ id: v.variant_id, label: v.product_name, sub: `${v.pack_type} · ${v.unit_size}`, qr: v.qr_value })));
    } else if (kind === "meal") {
      // iter20: Meal Record labels. Uses existing qr_value on each meal_record — no new QRs generated.
      const params = new URLSearchParams({ limit: "500" });
      if (q) params.set("q", q);
      const { data } = await api.get(`/meals?${params.toString()}`);
      setItems(data.map((m) => ({
        id: m.meal_record_id,
        label: `${m.diet_type} · ${m.diet_code}`,
        sub: `Wk ${m.cycle_week ?? "—"} · Pos ${m.cycle_position ?? "—"}`,
        qr: m.qr_value || `MR-${m.meal_record_id}`,
      })));
    } else {
      const { data } = await api.get("/locations");
      setItems(data.map((l) => ({ id: l.location_id, label: l.name, sub: `Bay ${l.bay}`, qr: l.qr_value })));
    }
    setSelected([]);
  }, [kind, q]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleAll = () => {
    if (selected.length === items.length) setSelected([]);
    else setSelected(items.map((i) => i.id));
  };

  const download = async () => {
    if (selected.length === 0) { toast.error("Select at least one item"); return; }
    try {
      const res = await api.post("/qr/labels/pdf", {
        kind, ids: selected, cols, rows, label_width_in: parseFloat(w), label_height_in: parseFloat(h),
      }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "labels.pdf"; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const generateMissing = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/qr/generate-missing", { kind });
      toast.success(`Generated QR for ${data.updated} ${kind}(s)`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setGenerating(false); }
  };

  return (
    <div className="p-4 md:p-6 max-w-6xl">
      <Link to="/admin" className="text-xs text-muted-foreground hover:text-primary">← Admin</Link>
      <div className="my-3">
        <div className="label-tech mb-1">Labels</div>
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">QR Print</h1>
      </div>

      <div className="card-flat border-border p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-[200px_1fr_auto] gap-2">
          <select value={kind} onChange={(e) => setKind(e.target.value)} className="h-11 px-2 border border-border bg-background rounded-sm text-sm" data-testid="qr-kind-select">
            <option value="variant">Item variants</option>
            <option value="meal">Meal records</option>
            <option value="location">Locations</option>
          </select>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-3.5 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" className="w-full h-11 pl-9 pr-3 border border-border bg-background rounded-sm text-sm" />
          </div>
          <button
            data-testid={IDS.qrGenerateMissing}
            onClick={generateMissing}
            disabled={generating}
            className="btn-hard h-11 px-4 border border-border rounded-sm text-xs font-bold uppercase tracking-widest hover:bg-muted"
          >
            {generating ? "…" : "Generate Missing"}
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
          <label className="text-xs">
            <div className="label-tech mb-1">Cols</div>
            <input type="number" min="1" value={cols} onChange={(e) => setCols(parseInt(e.target.value, 10))} className="w-full h-9 px-2 border border-border bg-background rounded-sm text-sm" />
          </label>
          <label className="text-xs">
            <div className="label-tech mb-1">Rows</div>
            <input type="number" min="1" value={rows} onChange={(e) => setRows(parseInt(e.target.value, 10))} className="w-full h-9 px-2 border border-border bg-background rounded-sm text-sm" />
          </label>
          <label className="text-xs">
            <div className="label-tech mb-1">Label W (in)</div>
            <input type="number" step="0.1" value={w} onChange={(e) => setW(e.target.value)} className="w-full h-9 px-2 border border-border bg-background rounded-sm text-sm" />
          </label>
          <label className="text-xs">
            <div className="label-tech mb-1">Label H (in)</div>
            <input type="number" step="0.1" value={h} onChange={(e) => setH(e.target.value)} className="w-full h-9 px-2 border border-border bg-background rounded-sm text-sm" />
          </label>
        </div>
      </div>

      <div className="card-flat border-border overflow-hidden">
        <div className="p-3 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={toggleAll} className="text-xs font-bold uppercase tracking-widest text-primary hover:underline">
              {selected.length === items.length && items.length > 0 ? "Deselect all" : "Select all"}
            </button>
            <span className="text-xs text-muted-foreground font-mono">{selected.length} selected · {items.length} shown</span>
          </div>
          <button
            data-testid={IDS.qrPrintDownload}
            onClick={download}
            className="btn-hard h-9 px-4 bg-primary text-primary-foreground font-bold uppercase tracking-widest text-xs rounded-sm hover:bg-orange-600 hover:text-white flex items-center gap-1"
          >
            <DownloadSimple size={14} weight="bold" /> Download PDF
          </button>
        </div>
        <div className="max-h-[60vh] overflow-auto">
          <table className="w-full text-sm" data-testid={IDS.qrPrintSelect}>
            <tbody className="divide-y divide-border">
              {items.map((it) => (
                <tr key={it.id} className="hover:bg-muted/30">
                  <td className="px-3 py-1.5 w-8">
                    <input
                      type="checkbox"
                      checked={selected.includes(it.id)}
                      onChange={() => setSelected((s) => s.includes(it.id) ? s.filter((x) => x !== it.id) : [...s, it.id])}
                    />
                  </td>
                  <td className="px-3 py-1.5">
                    <div className="font-medium">{it.label}</div>
                    <div className="text-[10px] font-mono text-muted-foreground">{it.qr}</div>
                  </td>
                  <td className="px-3 py-1.5 text-xs text-muted-foreground">{it.sub}</td>
                  <td className="px-3 py-1.5 w-16">
                    <img alt="qr" src={`${BACKEND_URL}/api/qr/png/${encodeURIComponent(it.qr)}?size=64`} className="w-10 h-10 border" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
